import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Activity, ArrowRight, Bot, ChartNoAxesCombined, Sparkles } from "lucide-react";
import { dashboardStats, growthOsActivity } from "@/components/equinox/data";

const phases = [
  { progress: [82, 68, 91], status: "Signal sync live" },
  { progress: [89, 74, 94], status: "AI optimisation running" },
  { progress: [94, 79, 97], status: "Growth engine compounding" },
];

const workflowNodes = [
  {
    key: "acquire",
    label: "Acquire",
    title: "Acquire predictably",
    text: "Paid media, creators, and targeting loops work together to bring in the right installs at the right cost.",
  },
  {
    key: "convert",
    label: "Convert",
    title: "Convert efficiently",
    text: "Store conversion, onboarding improvements, and landing-page clarity improve activation from every click.",
  },
  {
    key: "retain",
    label: "Retain",
    title: "Retain with trust",
    text: "Ratings, review systems, and lifecycle signals strengthen brand trust and repeat engagement.",
  },
  {
    key: "scale",
    label: "Scale",
    title: "Scale what works",
    text: "Winning signals roll into budget decisions, automation, and operational loops that compound performance.",
  },
];

export default function GrowthOsPreviewSection({ onOpenChatbot }) {
  const [phaseIndex, setPhaseIndex] = useState(0);
  const [activeNode, setActiveNode] = useState(0);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setPhaseIndex((current) => (current + 1) % phases.length);
    }, 2600);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const nodeTimer = window.setInterval(() => {
      setActiveNode((current) => (current + 1) % workflowNodes.length);
    }, 2200);
    return () => window.clearInterval(nodeTimer);
  }, []);

  const phase = phases[phaseIndex];
  const workflow = workflowNodes[activeNode];

  return (
    <section className="px-6 pb-8 pt-14 sm:px-8 lg:px-12 lg:pb-10 lg:pt-16" data-testid="growth-os-preview-section">
      <div className="mx-auto grid max-w-7xl gap-6 lg:grid-cols-[0.92fr_1.08fr]">
        <motion.div
          initial={{ opacity: 0, y: 22 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7 }}
          className="glitter-card glass-panel rounded-[2rem] p-8 lg:p-10"
        >
          <p className="text-xs uppercase tracking-[0.3em] text-white/[0.42]">Equinox Growth OS</p>
          <h2 className="mt-4 font-display text-3xl text-white sm:text-4xl lg:text-5xl">A live system layer built for scalable user acquisition.</h2>
          <p className="mt-5 max-w-xl text-base leading-8 text-white/[0.66]">
            Growth OS brings together automation, attribution, creative testing, and WhatsApp funnels into one product-style operating layer.
          </p>

          <div className="mt-8 grid gap-4 sm:grid-cols-2">
            {[
              { icon: Bot, title: "Automation engine", text: "Runs repetitive growth operations automatically." },
              { icon: ChartNoAxesCombined, title: "Attribution layer", text: "Turns channel signals into clearer decisions." },
            ].map(({ icon: Icon, title, text }) => (
              <div key={title} className="rounded-[1.5rem] border border-white/[0.08] bg-black/20 p-5">
                <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-2xl border border-white/[0.12] bg-white/[0.06] text-cyan-300">
                  <Icon className="h-5 w-5" />
                </div>
                <p className="font-display text-xl text-white">{title}</p>
                <p className="mt-3 text-sm leading-7 text-white/[0.62]">{text}</p>
              </div>
            ))}
          </div>

          <div className="workflow-preview-shell mt-8 rounded-[1.6rem] border border-white/[0.08] bg-black/20 p-5" data-testid="growth-os-workflow-preview">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.22em] text-white/[0.42]">Interactive workflow preview</p>
                <h3 className="mt-2 font-display text-2xl text-white">Growth nodes connected in sequence</h3>
              </div>
              <div className="rounded-full border border-cyan-300/25 bg-cyan-300/10 px-3 py-1 text-xs text-cyan-200">Live preview</div>
            </div>

            <div className="workflow-nodes mt-6">
              {workflowNodes.map((node, index) => (
                <button
                  key={node.key}
                  type="button"
                  onMouseEnter={() => setActiveNode(index)}
                  onFocus={() => setActiveNode(index)}
                  className={`workflow-node ${activeNode === index ? "active" : ""}`}
                  data-testid={`workflow-node-${node.key}`}
                >
                  <span className="workflow-node-dot" />
                  <span className="workflow-node-label">{node.label}</span>
                  {index < workflowNodes.length - 1 ? <span className="workflow-connector" /> : null}
                </button>
              ))}
            </div>

            <motion.div
              key={workflow.key}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, ease: "easeOut" }}
              className="mt-6 rounded-[1.35rem] border border-white/[0.08] bg-white/[0.04] p-4"
              data-testid="workflow-node-content"
            >
              <p className="font-display text-xl text-white">{workflow.title}</p>
              <p className="mt-3 text-sm leading-7 text-white/[0.66]">{workflow.text}</p>
            </motion.div>
          </div>

          <button
            type="button"
            onClick={onOpenChatbot}
            className="glass-button glass-panel mt-8 rounded-full px-6 py-4 text-sm font-medium text-white transition-all duration-300 hover:scale-[1.03]"
            data-testid="growth-os-preview-cta"
          >
            <span className="relative z-10 inline-flex items-center gap-2">
              Audit My App →
              <ArrowRight className="h-4 w-4" />
            </span>
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 26 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7, delay: 0.08 }}
          className="panel-scan glitter-card glass-panel rounded-[2rem] p-7"
        >
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-xs uppercase tracking-[0.28em] text-white/[0.42]">Live dashboard UI</p>
              <h3 className="mt-3 font-display text-2xl text-white">Growth signals updating in real time</h3>
            </div>
            <div className="status-pulse rounded-full border border-emerald-300/30 bg-emerald-300/10 px-3 py-1 text-xs text-emerald-200">
              {phase.status}
            </div>
          </div>

          <div className="mt-6 grid gap-4 md:grid-cols-3">
            {dashboardStats.map((stat, index) => (
              <div key={stat.label} className="rounded-[1.35rem] border border-white/[0.08] bg-black/20 p-4" data-testid={`growth-os-stat-${index + 1}`}>
                <p className="text-sm text-white/[0.48]">{stat.label}</p>
                <p className={`mt-3 bg-gradient-to-r ${stat.tone} bg-clip-text font-display text-3xl text-transparent`}>{stat.value}</p>
              </div>
            ))}
          </div>

          <div className="mt-6 space-y-4">
            {["Automation engine", "Attribution system", "WhatsApp funnel"].map((label, index) => (
              <div key={label} className="rounded-[1.35rem] border border-white/[0.08] bg-black/20 p-4">
                <div className="flex items-center justify-between text-sm text-white/[0.72]">
                  <span>{label}</span>
                  <span>{phase.progress[index]}%</span>
                </div>
                <div className="mt-3 h-2.5 overflow-hidden rounded-full bg-white/[0.08]">
                  <div className="progress-loop h-full rounded-full bg-gradient-to-r from-violet-400 via-sky-400 to-cyan-300" style={{ width: `${phase.progress[index]}%` }} />
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 rounded-[1.5rem] border border-white/[0.08] bg-white/[0.04] p-5">
            <div className="mb-4 flex items-center gap-2 text-sm text-cyan-300">
              <Activity className="h-4 w-4" />
              Activity feed
            </div>
            <div className="space-y-3 text-sm text-white/[0.68]">
              {growthOsActivity.map((item) => (
                <div key={item} className="flex items-start gap-3">
                  <Sparkles className="mt-0.5 h-4 w-4 text-violet-300" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}